import axios, { AxiosRequestConfig } from 'axios';
import { API_ENDPOINTS } from '@/constants/api-endpoint';
import { APIResponse } from '@/types/res';
import {apiClient} from './api';
import { forceLogout } from '@/utils/utils';

const API_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;

// Tạo instance của Axios
export const apiBackend = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000,
});

// Axios Request Interceptor
apiBackend.interceptors.request.use(async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }

    if (API_URL && API_URL.includes('localhost')) {
        const url = config.url || ""
        if (url.startsWith("/service/api/ra")) {
            try {
                // Không truyền explicit Authorization header, để apiClient interceptor tự động lấy token mới từ localStorage
                // Điều này cho phép refresh token hoạt động đúng khi token hết hạn
                const authRes = await apiClient.get('/ums/api/auth');

                const idToken = authRes.headers['x-id-token'] || authRes.headers['X-ID-Token'];
                const hmacSignature = authRes.headers['x-hmac-signature'] || authRes.headers['X-HMAC-Signature'];

                if (idToken && hmacSignature) {
                    config.headers['x-id-token'] = idToken;
                    config.headers['x-hmac-signature'] = hmacSignature;
                }
            } catch (err) {
                console.error('Lỗi khi lấy thông tin từ Service ums:', err);
                throw err;
            }
        }

    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Axios Response Interceptor (có thể dùng để refresh token)
apiBackend.interceptors.response.use(
    (response) => {
        if (response?.data.success !== undefined) {
            if (response.data.success) {
                return response;
            } else {
                return Promise.reject({
                    message: response.data.message || 'Yêu cầu không thành công',
                    code: response.data.code,
                    response,
                });
            }
        }
        return response.data;
    },
    async (error) => {
        const originalRequest = error.config;
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                try {
                    localStorage.setItem('token', refreshToken);
                    const { data } = await apiClient.post(API_ENDPOINTS.REFRESH_TOKEN, { refresh_token: refreshToken });
                    localStorage.setItem('token', data.data.access_token);
                    localStorage.setItem('refresh_token', data.data.refresh_token);
                    originalRequest.headers['Authorization'] = `Bearer ${data.data.access_token}`;
                    return apiBackend(originalRequest);
                } catch (err) {
                    console.error('Refresh token thất bại:', err);
                    forceLogout();
                    return Promise.reject(err);
                }
            } else {
                forceLogout();
                return Promise.reject(new Error('No refresh token available'));
            }
        }
        return Promise.reject(error);
    }
);

const handleError = (error: any) => {
    if (error.code === 'ECONNABORTED') {
        throw new Error('Hết thời gian yêu cầu: máy chủ không phản hồi kịp thời!');
    }
    if (error.response) {
        // Ném Error chứ không ném chuỗi: service bọc lại bằng `error?.message || '...'`, ném chuỗi là mất message server
        throw new Error(error.response.data?.message || 'Yêu cầu không thành công');
    } else if (error.request) {
        throw new Error(' Không có phản hồi từ máy chủ!');
    } else {
        throw new Error(' Lỗi thiết lập yêu cầu!');
    }
};

// Method GET
export const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiBackend.get(url, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method POST
export const post = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiBackend.post(url, data, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method PUT
export const put = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiBackend.put(url, data, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method PATCH
export const patch = async <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiBackend.patch(url, data, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method DELETE
export const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> => {
    try {
        const response = await apiBackend.delete(url, config);
        return response.data as APIResponse<T>;
    } catch (error) {
        throw handleError(error);
    }
};

// Method HEAD
export const head = async (url: string, config?: AxiosRequestConfig) => {
    try {
        return await apiBackend.head(url, config);
    } catch (error) {
        throw handleError(error);
    }
};